/**
 * Boundary between the UI's `QueryFootnote` display rows and the backend's
 * `QueryFootnoteType` / `QueryFootnoteInput` shapes.
 *
 * Read: `toQueryFootnoteRows` keeps the full raw `QueryFootnoteType` for each
 * row so the write path can re-serialize every footnote back to the backend
 * without losing fields the grid never displayed. Local mutations (add, edit,
 * delete) operate on these rows. Grid display strings are derived on demand
 * via `toFootnoteDisplay`.
 *
 * Write: `toQueryFootnoteInputs` serializes the raw companions into
 * `QueryFootnoteInput[]`, re-indexing `sortOrder` from row order.
 *
 */

import plur from "plur";
import { normalizeAttributePath } from "../../../shared/RulesSection/attributeCatalogue";
import type { QueryFootnote, QueryFootnoteTypeLabel } from "./types";
import type { RuleConditionSource, RuleSetSource } from "../../../shared/RulesSection/mappers";
import type {
  QueryFieldKind,
  QueryFootnoteFieldsFragment,
  QueryFootnoteInput,
  QueryFootnoteKind,
  RuleConditionInput,
  RuleGroupInput,
  RuleSetInput,
} from "~/graphql/supergraphTypes";

/** Maximum number of footnotes per query. */
export const MAX_FOOTNOTES = 50;

// -- Row type ----------------------------------------------------------------

/** Local footnote row. Holds the full raw `QueryFootnoteType` (not just the
 * fields the grid displays) so the buffered Save can re-serialize every
 * footnote back to the backend without losing fields the UI never showed.
 * Edits replace `raw` with an updated fragment; untouched rows keep their
 * original `raw` unchanged. */
export type QueryFootnoteRow = {
  raw: QueryFootnoteFieldsFragment;
};

// -- Linked data field -------------------------------------------------------

/** The reference columns a footnote uses to point at a query field. Footnotes
 * are now placed manually via the `{footnotesymbol.<name>}` merge field, so
 * these are only written cleared (`EMPTY_FOOTNOTE_LINK`) — the shape is kept
 * to null out any legacy link a persisted footnote still carries. */
export type FootnoteLink = {
  linkedKind: QueryFieldKind | null;
  linkedTypeHierarchyNodeId: number | null;
  linkedFormulaId: number | null;
  linkedPrimaryField: string | null;
  linkedFormulaDependencyId: number | null;
};

/** Every reference column cleared — written on save to unlink a footnote. */
export const EMPTY_FOOTNOTE_LINK: FootnoteLink = {
  linkedKind: null,
  linkedTypeHierarchyNodeId: null,
  linkedFormulaId: null,
  linkedPrimaryField: null,
  linkedFormulaDependencyId: null,
};

// -- Enum display maps -------------------------------------------------------

const FOOTNOTE_KIND_TO_LABEL: Record<QueryFootnoteKind, QueryFootnoteTypeLabel> = {
  FREEFORM_TEXT: "Freeform text",
  CUSTOM_FIELD: "Custom field",
};

// -- Display summaries -------------------------------------------------------

const summarizeRules = (ruleSet: RuleSetSource | null | undefined): string => {
  if (!ruleSet || ruleSet.groups.length === 0) return "None";
  const count = ruleSet.groups.reduce((sum, group) => sum + group.conditions.length, 0);
  if (count === 0) return "None";
  return `${count} ${plur("rule", count)}`;
};

// -- Symbol merge field ------------------------------------------------------

/** Stand-in for a merge-field token that doesn't exist yet*/
export const MERGE_FIELD_PLACEHOLDER = "N/A";

const SYMBOL_TOKEN_RE = /^\{footnotesymbol\.([a-z][a-z0-9_]{0,62})\}$/;

/**
 * Turn the server's bare `{footnotesymbol.<slug>}` into the template-ready
 * Carbone tag an author pastes into a document: `{d.footnotesymbol.<slug>}`.
 */
export const toSymbolTemplateTag = (symbolMergeField: string): string =>
  SYMBOL_TOKEN_RE.test(symbolMergeField)
    ? `{d.${symbolMergeField.slice("{".length)}`
    : symbolMergeField;

// -- Read path ---------------------------------------------------------------

/** Build the display row for a footnote. */
export const toFootnoteDisplay = (raw: QueryFootnoteFieldsFragment): QueryFootnote => ({
  id: String(raw.id),
  name: raw.name,
  symbolMergeFieldPreview: toSymbolTemplateTag(raw.symbolMergeField),
  footnoteType: FOOTNOTE_KIND_TO_LABEL[raw.footnoteKind],
  suppressionLogic: summarizeRules(raw.suppressionRules),
});

/** Build an empty `FREEFORM_TEXT` footnote fragment for the create flow.
 * `id` is a caller-supplied negative placeholder (the backend assigns the
 * real id on save); `queryVersionId` is irrelevant to the write path
 * (`toQueryFootnoteInputs` drops it) so it defaults to 0. The slug and the
 * token composed from it are empty for the same reason — the server mints
 * them on save. */
export const createBlankFootnoteRaw = (id: number): QueryFootnoteFieldsFragment => ({
  __typename: "QueryFootnoteType",
  id,
  queryVersionId: 0,
  footnoteKind: "FREEFORM_TEXT",
  name: "",
  mergeFieldSlug: "",
  symbolMergeField: "",
  text: "",
  customFieldId: null,
  ...EMPTY_FOOTNOTE_LINK,
  overrideFootnoteSymbol: false,
  footnoteSymbolStyle: null,
  sortOrder: 0,
  suppressionRules: { __typename: "RuleSetType", groupsOperator: "AND", groups: [] },
});

const bySortOrder = (a: QueryFootnoteFieldsFragment, b: QueryFootnoteFieldsFragment): number =>
  a.sortOrder - b.sortOrder;

/** Convert the `finpubQuery.footnotes` array into local rows, ordered by
 * `sortOrder`. Display strings are derived later via `toFootnoteDisplay`. */
export const toQueryFootnoteRows = (
  footnotes: ReadonlyArray<QueryFootnoteFieldsFragment> | null | undefined,
): QueryFootnoteRow[] => {
  if (!footnotes) return [];
  return [...footnotes].sort(bySortOrder).map((raw) => ({ raw }));
};

/** Reorder rows to match `orderedIds` (from a drag gesture); identity is the
 * stringified `raw.id`, matching the grid's `getRowId`. If `orderedIds` doesn't
 * cover exactly the current rows, the order is returned unchanged so a partial
 * reorder can't drop or duplicate a row. */
export const reorderQueryFootnoteRows = (
  rows: ReadonlyArray<QueryFootnoteRow>,
  orderedIds: ReadonlyArray<string>,
): QueryFootnoteRow[] => {
  if (orderedIds.length !== rows.length) return [...rows];
  const byId = new Map(rows.map((row) => [String(row.raw.id), row]));
  const next: QueryFootnoteRow[] = [];
  for (const id of orderedIds) {
    const row = byId.get(id);
    if (!row) return [...rows];
    next.push(row);
  }
  return next;
};

// -- Write path --------------------------------------------------------------
// Display rows are lossy (they carry only what the grid shows), so the write
// side serializes the raw companion.

const toRuleConditionInput = (condition: RuleConditionSource): RuleConditionInput => {
  const attribute = typeof condition.attribute === "string" ? condition.attribute : "";
  return {
    attribute: normalizeAttributePath(attribute),
    operator: condition.operator,
    value: condition.value,
    // A `RUNTIME_INPUT` condition's value is a token name rather than a
    // filter value; dropping the kind here would make the backend validate
    // the token as a literal and reject it.
    valueKind: condition.valueKind ?? "LITERAL",
    sortOrder: condition.sortOrder,
  };
};

const toRuleGroupInput = (group: RuleSetSource["groups"][number]): RuleGroupInput => ({
  conditionOperator: group.conditionOperator,
  sortOrder: group.sortOrder,
  conditions: group.conditions.map(toRuleConditionInput),
});

const toRuleSetInput = (ruleSet: RuleSetSource | null | undefined): RuleSetInput | null => {
  if (!ruleSet) return null;
  return {
    groupsOperator: ruleSet.groupsOperator,
    groups: ruleSet.groups.map(toRuleGroupInput),
  };
};

const toInput = (raw: QueryFootnoteFieldsFragment, sortOrder: number): QueryFootnoteInput => ({
  footnoteKind: raw.footnoteKind,
  name: raw.name,
  // The slug — not the name — is what identifies a footnote to the server, so
  // echoing back the one it issued is what lets a rename keep the token an
  // author has already pasted into a template. A footnote that has never been
  // saved has no slug yet, and the empty string asks the server to mint one.
  mergeFieldSlug: raw.mergeFieldSlug,
  // The backend rejects a CUSTOM_FIELD footnote that sets `text` (it derives
  // its body from the linked custom field), so force it null on the wire —
  // guarding against any raw that carried an empty string through.
  text: raw.footnoteKind === "CUSTOM_FIELD" ? null : raw.text,
  // Send the string-serialized id — the Int `customFieldId` input is
  // deprecated because a warehouse custom-field id can overflow 32 bits.
  customFieldIdStr: raw.customFieldId,
  linkedKind: raw.linkedKind,
  linkedTypeHierarchyNodeId: raw.linkedTypeHierarchyNodeId,
  linkedFormulaId: raw.linkedFormulaId,
  // `linkedPrimaryField` is the canonical "<table>.<column>" input —
  // `linkedPrimaryFieldId` is deprecated on both read and write.
  linkedPrimaryField: raw.linkedPrimaryField,
  linkedFormulaDependencyId: raw.linkedFormulaDependencyId,
  overrideFootnoteSymbol: raw.overrideFootnoteSymbol,
  footnoteSymbolStyle: raw.footnoteSymbolStyle,
  sortOrder,
  suppressionRules: toRuleSetInput(raw.suppressionRules),
});

/** Re-assigns `sortOrder` from row order so reordering (future sub-task)
 * persists without callers threading indices. */
export const toQueryFootnoteInputs = (
  rows: ReadonlyArray<QueryFootnoteRow>,
): QueryFootnoteInput[] => rows.map((row, index) => toInput(row.raw, index));
